import { Block, Flex, SizeProperty, Typography } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { Textarea } from '@aloft/toolkit-sui/chakra';

import { Control, Controller, FieldPath, FieldValues, useWatch } from 'react-hook-form';

import { useRequiredFieldAlertInteraction } from '../../modules/alerts/useRequiredFieldAlertInteraction.ts';
import {
  ALERT_TARGET_FIELD_TITLE,
  getAlertTargetInputFieldSx,
} from '../PropertyGrid/fields/styles.ts';

import '../../modules/alerts/requiredFieldAlertHighlight.scss';

type FormTextareaProps<T extends FieldValues> = {
  name: FieldPath<T>;
  control: Control<T>;
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  labelWidth?: string;
  column?: boolean;
  inputWidth?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  resize?: 'none' | 'horizontal' | 'vertical' | 'both';
  customErrorMessage?: string;
  onClear?: () => void;
  labelVariant?: 'h4' | 'h3' | 'label';
  labelColor?: 'textSecondary' | 'textTertiary' | 'textPrimary';
  height?: string;
  subHeaderContent?: React.ReactNode;
  requiredFieldName?: string;
};

export const FormTextarea = <T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  isRequired = false,
  labelWidth = '250px',
  column = false,
  inputWidth = '500px',
  size = 'sm',
  resize = 'vertical',
  customErrorMessage,
  onClear,
  labelVariant = 'h4',
  labelColor = 'textSecondary',
  height,
  subHeaderContent,
  requiredFieldName,
}: FormTextareaProps<T>) => {
  const { containerRef, isAlertTarget, isAlertHighlighted, clearAlertTarget } =
    useRequiredFieldAlertInteraction(requiredFieldName ?? '');
  const fieldValue = useWatch({ control, name, disabled: !requiredFieldName });
  const isFieldComplete = Boolean(fieldValue?.toString().trim());
  const showAlertHighlight = Boolean(
    requiredFieldName && isAlertHighlighted && !isFieldComplete,
  );

  const content = (
    <Flex flexDirection={column ? 'column' : 'row'}>
      <Block
        width={column ? '100%' : (labelWidth as SizeProperty)}
        marginBottom={column ? '0.5rem' : undefined}
      >
        {label && (
          <Typography variant={labelVariant} color={labelColor}>
            <>
              {label}
              {isRequired && <span style={{ color: 'red' }}> *</span>}
            </>
          </Typography>
        )}
      </Block>
      {subHeaderContent}
      <Controller
        name={name}
        control={control}
        rules={{
          required: isRequired,
        }}
        render={({ field, fieldState }) => {
          const showAlertStyle = showAlertHighlight;
          const formError = fieldState.error?.message;
          const customError = customErrorMessage;
          const hasError = formError || customError;
          const shouldShowError =
            (fieldState.isTouched && hasError) ||
            (Boolean(requiredFieldName && isAlertHighlighted) && hasError);
          const fieldAlertSx = showAlertStyle
            ? getAlertTargetInputFieldSx(true, 'error', true)
            : undefined;

          return (
            <Block width={column ? '100%' : (inputWidth as SizeProperty)}>
              <Textarea
                size={size}
                placeholder={placeholder}
                isInvalid={!!shouldShowError}
                sx={fieldAlertSx}
                title={showAlertStyle ? ALERT_TARGET_FIELD_TITLE : undefined}
                height={height}
                resize={resize}
                {...field}
                onFocus={() => {
                  if (isAlertTarget) {
                    clearAlertTarget();
                  }
                }}
                onBlur={field.onBlur}
                onChange={(e) => {
                  field.onChange(e);
                  if (onClear && e.target.value === '') {
                    onClear();
                  }
                }}
              />
              {shouldShowError && (
                <Typography variant="body2" color="error">
                  {formError || customError}
                </Typography>
              )}
            </Block>
          );
        }}
      />
    </Flex>
  );

  if (requiredFieldName) {
    return (
      <Block
        ref={containerRef}
        data-required-field={requiredFieldName}
        data-required-field-alert-highlight={showAlertHighlight ? '' : undefined}
      >
        {content}
      </Block>
    );
  }

  return content;
};
